import { FC, useEffect, useMemo, useState } from "react";
import classes from './idmRolesWidget.module.scss'
import { idmService, IIdmFeature, IIdmRole } from "../../../../entities/idm";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { SearchItems } from "../../../../features/searchItems";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { Modal } from "../../../../shared/ui/modal";
import { MyCheckbox } from "../../../../shared/ui/myCheckbox/myCheckbox";
import { ConfirmationAction } from "../../../../shared/ui/confirmationAction";
import { useAppSelector } from "../../../../app/store/store";

export const IdmRolesWidget: FC = () => {

    const getRoleTitle = (role: IIdmRole) => role.name.trim().split(/\s+/)[0] || role.name

    const [roles, setRoles] = useState<IIdmRole[]>([])
    const [roleSearchItems, setRoleSearchItems] = useState<Array<IIdmRole & {name: string}>>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [selectedRole, setSelectedRole] = useState<IIdmRole | null>(null)
    const [roleFeatures, setRoleFeatures] = useState<IIdmFeature[]>([])
    const [allFeatures, setAllFeatures] = useState<IIdmFeature[]>([])
    const [featureSearchItems, setFeatureSearchItems] = useState<Array<IIdmFeature & {name: string}>>([])
    const [isRoleFeaturesLoading, setIsRoleFeaturesLoading] = useState<boolean>(false)
    const [pendingFeatureAction, setPendingFeatureAction] = useState<{feature: IIdmFeature; nextChecked: boolean} | null>(null)
    const [isActionLoading, setIsActionLoading] = useState<boolean>(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canListRoleFeatures = hasFeature(my, FEATURES.IDM.ROLE_FEATURES_LIST)
    const canAddRoleFeatures = hasFeature(my, FEATURES.IDM.ROLE_FEATURES_ADD)
    const canRemoveRoleFeatures = hasFeature(my, FEATURES.IDM.ROLE_FEATURES_REMOVE)

    const handleError = (e: unknown, message: string) => {
        console.log(e)
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message || message, type: 'error'})
        }
        else{
            setGlobalMessage({message, type: 'error'})
        }
    }

    useEffect(() => {
        const loadRoles = async () => {
            try{
                setIsLoading(true)
                const rolesRes = await idmService.listRoles()
                const sortedRoles = rolesRes.sort((a, b) => a.hierarchy_level - b.hierarchy_level || a.name.localeCompare(b.name))
                setRoles(sortedRoles)
                setRoleSearchItems(sortedRoles.map(role => ({
                    ...role,
                    name: `${role.name} ${role.code} ${role.description} ${role.hierarchy_level}`
                })))
            }
            catch(e){
                handleError(e, 'Ошибка при получении справочника ролей')
            }
            finally{
                setIsLoading(false)
            }
        }

        loadRoles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectedFeatureIds = useMemo(
        () => new Set(roleFeatures.map(feature => feature.id)),
        [roleFeatures]
    )

    const syncFeatureSearch = (features: IIdmFeature[]) => {
        setFeatureSearchItems(features.map(feature => ({
            ...feature,
            name: `${feature.name} ${feature.key} ${feature.description} ${feature.category}`
        })))
    }

    const onRoleOpen = async (role: IIdmRole) => {
        if(!canListRoleFeatures){
            setGlobalMessage({message: 'Нет доступа к просмотру фич роли', type: 'error'})
            return
        }
        try{
            setSelectedRole(role)
            setIsRoleFeaturesLoading(true)
            const [roleFeaturesRes, allFeaturesRes] = await Promise.all([
                idmService.listRoleFeatures(role.id),
                idmService.listFeatures()
            ])
            setRoleFeatures(roleFeaturesRes.features)
            setAllFeatures(allFeaturesRes.features)
            syncFeatureSearch(allFeaturesRes.features)
        }
        catch(e){
            handleError(e, 'Ошибка при получении фич роли')
        }
        finally{
            setIsRoleFeaturesLoading(false)
        }
    }

    const onRoleClose = (open: boolean) => {
        if(!open){
            setSelectedRole(null)
            setRoleFeatures([])
            setAllFeatures([])
            setFeatureSearchItems([])
            setPendingFeatureAction(null)
        }
    }

    const onFeatureSelected = (feature: IIdmFeature) => {
        return (nextChecked: boolean) => {
            if((nextChecked && !canAddRoleFeatures) || (!nextChecked && !canRemoveRoleFeatures)){
                setGlobalMessage({
                    message: nextChecked ? 'Нет доступа к добавлению фичи в роль' : 'Нет доступа к удалению фичи из роли',
                    type: 'error'
                })
                return
            }
            setPendingFeatureAction({feature, nextChecked})
        }
    }

    const onFeatureActionConfirm = async () => {
        if(!selectedRole || !pendingFeatureAction){
            return
        }

        try{
            setIsActionLoading(true)
            if(pendingFeatureAction.nextChecked){
                await idmService.addRoleFeature(selectedRole.id, pendingFeatureAction.feature.id)
                setGlobalMessage({message: 'Фича успешно добавлена в роль', type: 'ok'})
            }
            else{
                await idmService.removeRoleFeature(selectedRole.id, pendingFeatureAction.feature.id)
                setGlobalMessage({message: 'Фича успешно удалена из роли', type: 'ok'})
            }

            const roleFeaturesRes = await idmService.listRoleFeatures(selectedRole.id)
            setRoleFeatures(roleFeaturesRes.features)
            setPendingFeatureAction(null)
        }
        catch(e){
            handleError(e, pendingFeatureAction.nextChecked ? 'Ошибка при добавлении фичи в роль' : 'Ошибка при удалении фичи из роли')
        }
        finally{
            setIsActionLoading(false)
        }
    }

    const pendingActionTitle = pendingFeatureAction
        ? pendingFeatureAction.nextChecked
            ? `Добавить фичу "${pendingFeatureAction.feature.name}" в роль? Изменение применится ко всем пользователям этой роли.`
            : `Удалить фичу "${pendingFeatureAction.feature.name}" из роли? Изменение применится ко всем пользователям этой роли.`
        : ''

    return (
        <section className={classes.container}>
            <section className={classes.search}>
                <SearchItems
                    placeholder="Поиск по названию, коду или описанию роли"
                    items={roles.map(role => ({
                        ...role,
                        name: `${role.name} ${role.code} ${role.description} ${role.hierarchy_level}`
                    }))}
                    setItems={setRoleSearchItems}
                />
            </section>
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {
                        roleSearchItems.length
                            ?
                        roleSearchItems.map(role => (
                            <section className={classes.card} key={role.id} onClick={() => onRoleOpen(role)}>
                                <section className={classes.head}>
                                    <section className={classes.name}>{getRoleTitle(role)}</section>
                                    <section className={classes.meta}>
                                        <span className={classes.badge}>code: {role.code || '—'}</span>
                                        <span className={classes.badge}>уровень: {role.hierarchy_level}</span>
                                        <span className={classes.badge}>ID: {role.id}</span>
                                    </section>
                                </section>
                                <section className={classes.description}>
                                    {role.description || 'Описание роли не указано'}
                                </section>
                            </section>
                        ))
                            :
                        <section className={classes.empty}>Роли не найдены</section>
                    }
                </section>
            }
            <Modal open={Boolean(selectedRole)} setOpen={onRoleClose}>
                <section className={classes.modalInner}>
                    <section className={classes.modalTitle}>
                        {selectedRole ? getRoleTitle(selectedRole) : 'Роль'}
                    </section>
                    <section className={classes.modalDescription}>
                        Выберите фичи роли. Любое добавление или удаление будет применено ко всем пользователям, у которых назначена эта роль.
                    </section>
                    <section className={classes.modalSearch}>
                        <SearchItems
                            placeholder="Поиск по названию, key, категории или описанию фичи"
                            items={allFeatures.map(feature => ({
                                ...feature,
                                name: `${feature.name} ${feature.key} ${feature.description} ${feature.category}`
                            }))}
                            setItems={setFeatureSearchItems}
                        />
                    </section>
                    {
                        isRoleFeaturesLoading
                            ?
                        <section className={classes.loader}><LoaderSpinner /></section>
                            :
                        <section className={classes.featureList}>
                            {
                                featureSearchItems.length
                                    ?
                                featureSearchItems.map(feature => (
                                    <section className={classes.featureItem} key={feature.id}>
                                        {canAddRoleFeatures || canRemoveRoleFeatures ? (
                                            <MyCheckbox
                                                checked={selectedFeatureIds.has(feature.id)}
                                                onSelected={onFeatureSelected(feature)}
                                                checkBoxTop
                                            >
                                                <section className={classes.featureContent}>
                                                    <section className={classes.featureHeader}>
                                                        <span className={classes.featureName}>{feature.name}</span>
                                                        <span className={classes.featureKey}>{feature.key}</span>
                                                    </section>
                                                    <section className={classes.featureMeta}>{feature.category || 'Без категории'}</section>
                                                    <section className={classes.featureMeta}>{feature.description || 'Описание отсутствует'}</section>
                                                </section>
                                            </MyCheckbox>
                                        ) : (
                                            <section className={classes.featureContent}>
                                                <section className={classes.featureHeader}>
                                                    <span className={classes.featureName}>{feature.name}</span>
                                                    <span className={classes.featureKey}>{feature.key}</span>
                                                </section>
                                                <section className={classes.featureMeta}>{feature.category || 'Без категории'}</section>
                                                <section className={classes.featureMeta}>{feature.description || 'Описание отсутствует'}</section>
                                            </section>
                                        )}
                                    </section>
                                ))
                                    :
                                <section className={classes.modalEmpty}>Фичи не найдены</section>
                            }
                        </section>
                    }
                </section>
            </Modal>
            <Modal open={Boolean(pendingFeatureAction)} setOpen={() => setPendingFeatureAction(null)}>
                <ConfirmationAction
                    title={pendingActionTitle}
                    type={pendingFeatureAction?.nextChecked ? 'send' : 'delete'}
                    onClick={onFeatureActionConfirm}
                    setOpen={() => setPendingFeatureAction(null)}
                    isLoading={isActionLoading}
                />
            </Modal>
        </section>
    )
}
